<template>
  <li
    v-if="shouldRenderMessage"
    :id="`message${data.id}`"
    ref="message"
    class="email--message"
  >
    <div class="flex justify-center w-full">
      <EmailMessagePrivateNote v-if="data.private" :message-data="data" />
      <div v-else-if="data.sender" class="w-full">
        <email-message-item
          :message-data="data"
          :message-content="message"
          :private-notes="privateNotes"
          :deleteable="deleteable"
          :assignable="assignable"
          :d99d-features="d99dFeatures"
          :inbox-supports-reply-to="inboxSupportsReplyTo"
          @forward="handleForwardTo"
        />
      </div>
      <system-message
        v-else-if="isActivity"
        :message-content="message"
        :message-data="data"
      />
      <spinner v-if="isPending" size="tiny" />
    </div>
    <div>
      <span
        v-if="isPending && hasAttachments"
        class="chat-bubble has-attachment agent"
      >
        {{ $t('CONVERSATION.UPLOADING_ATTACHMENTS') }}
      </span>
    </div>
  </li>
</template>
<script>
import { computed } from 'vue';
import messageFormatterMixin from 'shared/mixins/messageFormatterMixin';
import timeMixin from '../../../../mixins/time';
import Spinner from 'shared/components/Spinner.vue';
import alertMixin from 'shared/mixins/alertMixin';
import contentTypeMixin from 'shared/mixins/contentTypeMixin';
import { MESSAGE_TYPE, MESSAGE_STATUS } from 'shared/constants/messages';
import { generateBotMessageContent } from '../../../../components/widgets/conversation/helpers/botMessageContentHelper';
import { BUS_EVENTS } from 'shared/constants/busEvents';

import SystemMessage from '../SystemMessage.vue';
import EmailMessageItem from './EmailMessageItem.vue';
import EmailMessagePrivateNote from './EmailMessagePrivateNote.vue';

export default {
  name: 'EmailMessage',
  components: {
    Spinner,
    SystemMessage,
    EmailMessageItem,
    EmailMessagePrivateNote,
  },
  mixins: [alertMixin, timeMixin, messageFormatterMixin, contentTypeMixin],
  provide() {
    return {
      isPending: computed(() => this.isPending),
      hasAttachments: computed(() => this.hasAttachments),
    };
  },
  props: {
    data: {
      type: Object,
      required: true,
    },
    isATweet: {
      type: Boolean,
      default: false,
    },
    deleteable: {
      type: Boolean,
      default: false,
    },
    assignable: {
      type: Boolean,
      default: false,
    },
    d99dFeatures: {
      // d99d
      type: Object,
      default: () => ({}),
    },
    inboxSupportsReplyTo: {
      type: Boolean,
      default: false,
    },
    inReplyTo: {
      type: Object,
      default: () => ({}),
    },
    privateNotes: {
      type: Array[Object],
      default: () => [],
    },
  },
  data() {
    return {
      showContextMenu: false,
      contextMenuPosition: {},
      showBackgroundHighlight: false,
      d99dEnables: { empty: true }, // d99d
    };
  },
  computed: {
    shouldRenderMessage() {
      return (
        this.hasAttachments ||
        this.data.content ||
        this.isEmailContentType ||
        this.isAnIntegrationMessage ||
        ['referral', 'buttons', 'contact'].includes(this.contentType)
      );
    },
    emailMessageContent() {
      const {
        html_content: { full: fullHTMLContent } = {},
        text_content: { full: fullTextContent } = {},
      } = this.contentAttributes.email || {};
      return fullHTMLContent || fullTextContent || '';
    },
    message() {
      // If the message is an email, emailMessageContent would be present
      // In that case, we would use letter package to render the email
      if (this.emailMessageContent /* && this.isIncoming d99d commented */) {
        return this.emailMessageContent;
      }

      if (this.contentAttributes.forwarded) {
        return this.contentAttributes.forward_html_content || this.data.content;
      }

      const botMessageContent = generateBotMessageContent(
        this.contentType,
        this.contentAttributes,
        {
          noResponseText: this.$t('CONVERSATION.NO_RESPONSE'),
          csat: {
            ratingTitle: this.$t('CONVERSATION.RATING_TITLE'),
            feedbackTitle: this.$t('CONVERSATION.FEEDBACK_TITLE'),
          },
        }
      );

      if (this.contentType === 'input_csat') {
        return this.$t('CONVERSATION.CSAT_REPLY_MESSAGE') + botMessageContent;
      }

      return (
        this.formatMessage(this.data.content, false, this.data.private, false) +
        botMessageContent
      );
    },
    contentAttributes() {
      return this.data.content_attributes || {};
    },
    contentType() {
      const {
        data: { content_type: contentType },
      } = this;
      return contentType;
    },
    isActivity() {
      return this.data.message_type === MESSAGE_TYPE.ACTIVITY;
    },
    isAnIntegrationMessage() {
      return this.contentType === 'integrations';
    },
    hasAttachments() {
      return !!(this.data.attachments && this.data.attachments.length > 0);
    },
    isPending() {
      return this.data.status === MESSAGE_STATUS.PROGRESS;
    },
  },
  mounted() {
    this.$nextTick(() => {
      const telLinks = this.$refs.message?.querySelectorAll('a[href^="tel:"]');
      const emailLinks =
        this.$refs.message?.querySelectorAll('a[href^="mailto:"]');
      telLinks?.forEach(link => {
        link.addEventListener('click', this.handleTelLinkClick);
      });
      emailLinks?.forEach(link => {
        link.addEventListener('click', this.handleEmailLinkClick);
      });
    });
  },
  methods: {
    handleTelLinkClick(event) {
      event.preventDefault();
      const phoneNumber = event.target.href.replace('tel:', '');
      const phoneNumberTxt = event.target.textContent;
      bus.$emit(BUS_EVENTS.START_NEW_CONVERSATION, {
        phoneNumber,
        name: this.getNameForPhone(phoneNumberTxt),
      });
    },
    handleEmailLinkClick(event) {
      event.preventDefault();
      const email = event.target.href.replace('mailto:', '');
      bus.$emit(BUS_EVENTS.START_NEW_CONVERSATION, {
        email,
        name: this.getNameForPhone(email),
      });
    },
    getNameForPhone(phoneNumber = '') {
      const content = this.data.content.replace(/\\n/gm, '\n ');
      // Escape special regex characters in the phone number
      const escapedPhoneNumber = phoneNumber.replace(
        /[.*+?^${}()|[\]\\]/g,
        '\\$&'
      );
      // Regex to capture name with potential company details immediately before a phone number
      const regex = new RegExp(
        `([א-תA-Za-z]+(?:[א-תA-Za-z0-9]*\\s+)*)(?=${escapedPhoneNumber})`
      );
      let match = content.match(regex);
      if (match) {
        // Capture the most probable name part, trimming unnecessary spaces
        return match[1].trim();
      }
      return ''; // Fallback to empty if no name found
    },
    handleForwardTo() {
      this.$emit('forward', this.data);
    },
  },
};
</script>
<style lang="scss" scoped>
.reply-button {
  @apply border-solid;
  margin: 0px;
  margin-right: 2px;
  border-color: #e0e0e0 !important;
  color: black !important;
  align-items: center;
  box-sizing: border-box;
  display: inline-flex;
  border-radius: 4px;
  color: unset;
  margin-left: 4px;
  justify-content: center;
  text-decoration-line: none;
  vertical-align: middle;
  overflow: hidden;

  outline-style: none;
  padding: 5px 12px;
  min-width: 96px;
}
.email--message {
  .activity-wrap {
    width: 100%;
  }
  .wrap,
  .bubble {
    width: 100%;
    max-width: 100%;
    @apply mx-0;
    @apply border-b border-solid border-slate-700;
  }
  .bubble.is-text,
  .bubble {
    @apply p-1;
    @apply text-black-800 relative border-0 bg-white rounded-none border-slate-700;
  }
  ::v-deep {
    .reply-body,
    .reply-author {
      @apply text-black-800 relative border-b border-solid bg-white  border-slate-800;
    }
    .vert-line {
      @apply bg-slate-600;
    }
  }
}

.is-image {
  .message-text--metadata {
    position: relative;
    width: 100%;
    padding-top: 1rem;
  }
}
.context-menu {
  color: black;
}

.email-action-buttons {
  @apply flex flex-row justify-start gap-1;
}

[dir='rtl'] .email-action-buttons {
  @apply flex flex-row-reverse justify-start gap-1;
}

::v-deep {
  [id*='mail-editor-reference-message-container']:has(
      hr,
      [id*='divRplyFwdMsg'],
      div[style*='border-top'],
      blockquote
    ) {
    display: none !important;
  }

  [id*='divRplyFwdMsg'] {
    display: none !important;
  }

  div[style*='border-top: solid #e1e1e1'],
  div[style*='border-top: solid #b5c4df'],
  div[style*='border-top:solid #e1e1e1'],
  div[style*='border-top:solid #b5c4df'],
  div[style*='border-top: 1pt solid'],
  div[style*='border-top:1pt solid'] {
    &:has(b) {
      display: none !important;

      & ~ * {
        display: none !important;
      }
    }
  }

  .gmail_quote,
  [class*='gmail_quote'],
  div[class*='gmail_extra'],
  blockquote[class*='gmail_quote'] {
    display: none !important;
  }

  blockquote[type='cite'],
  blockquote[cite] {
    display: none !important;
  }

  [id*='yahoo_quoted'],
  blockquote[class*='yahoo_quoted'] {
    display: none !important;
  }

  .text-content blockquote:not([class*='user-quote']) {
    display: none !important;
  }
}
</style>
